import { useMemo } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { FetchRequest } from '@rails/request.js'

const PAGE_SIZE = 50

function fetchColumnCards(columnId, after, q) {
  const params = new URLSearchParams()
  if (after) params.set('after', after)
  params.set('limit', PAGE_SIZE)
  if (q) params.set('q', q)
  const url = `/kanban_columns/${columnId}/cards?${params.toString()}`
  const request = new FetchRequest('get', url, {
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
  })
  return request.perform().then(r => r.json)
}

export function columnCardsQueryKey(columnId) {
  return ['kanbanColumn', columnId, 'cards']
}

function useColumnCards(column, searchQuery = '') {
  const queryClient = useQueryClient()
  const columnId = column?.id
  const q = (searchQuery || '').trim()
  const isSearchMode = q.length > 0

  const seededInitialData = useMemo(() => {
    if (!column || isSearchMode) return undefined
    return {
      pages: [{
        cards: column.cards || [],
        next_cursor: column.next_cursor || null,
        total_count: column.total_count ?? (column.cards?.length || 0),
        unseen_count: column.unseen_count ?? 0
      }],
      pageParams: [null]
    }
  }, [columnId, isSearchMode])

  const queryKey = useMemo(
    () => [...columnCardsQueryKey(columnId), q || null],
    [columnId, q]
  )

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchColumnCards(columnId, pageParam, q),
    initialPageParam: null,
    getNextPageParam: lastPage => lastPage?.next_cursor || undefined,
    enabled: !!columnId,
    initialData: seededInitialData,
    staleTime: 30_000
  })

  const cards = useMemo(
    () => (query.data?.pages || []).flatMap(p => p.cards || []),
    [query.data]
  )

  const firstPage = query.data?.pages?.[0]
  const totalCount = firstPage?.total_count ?? cards.length
  const unseenCount = firstPage?.unseen_count ?? 0

  return {
    cards,
    totalCount,
    loadedCount: cards.length,
    unseenCount,
    isSearchMode,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    setColumnCardsCache: (updater) => {
      queryClient.setQueryData(columnCardsQueryKey(columnId), updater)
    }
  }
}

export default useColumnCards
